import { createContext, useContext } from 'react';
import type { EventFormValues } from '../schemas/eventSchema';
import type { Event } from 'commoncal-shared';

export interface EventPanelState {
  mode: 'create' | 'edit';
  /** Currently loaded event data when mode is 'edit'. */
  event: Event | null;
  /** Form default values derived from the event or create date. */
  defaultValues: EventFormValues;
  /** Whether the user is allowed to edit/delete this event. */
  canEdit: boolean;
  /** Whether the panel is currently in edit mode. */
  isEditing: boolean;
  /** Accent color for the active calendar. */
  accentColor: string;
  /** Name of the active calendar. */
  calendarName?: string;
  /** Whether the active calendar is a kocal. */
  isKocal: boolean;
  /** The user's role on the active calendar. */
  userRole?: string | null;
  /** Whether a save/delete is in progress. */
  isPending: boolean;
  /** Whether recurrence controls are disabled (single instance edit). */
  recurrenceDisabled: boolean;
  /** Switch from view mode to edit mode. */
  enterEditMode: () => void;
  /** Switch from edit mode back to view mode. */
  exitEditMode: () => void;
  /** Close the panel entirely. */
  onClose: () => void;
  /** Submit handler for the edit/create form. */
  onSubmit: (values: EventFormValues) => void;
  /** Extra content rendered inside the form (recurrence scope, mirrors, delete). */
  extraContent?: React.ReactNode;
}

const EventPanelContext =
  createContext<EventPanelState | null>(null);

export function EventPanelProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: EventPanelState;
}) {
  return (
    <EventPanelContext.Provider value={value}>
      {children}
    </EventPanelContext.Provider>
  );
}

export function useEventPanel() {
  const context = useContext(EventPanelContext);
  if (!context) {
    throw new Error(
      'useEventPanel must be used within an EventPanelProvider'
    );
  }
  return context;
}
